import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getEngagementInsights, GetEngagementInsightsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Shield, Star, MessageSquare, Megaphone, Gift, TrendingUp, Users } from 'lucide-react';
import { isManagerPlus } from '@/lib/roles';

type Data = GetEngagementInsightsOutputType;

function StatCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: React.FC<{ className?: string }>; color?: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color || 'text-primary'}`} />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function BarChart({ data, label }: { data: { name: string; count: number }[]; label: string }) {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{label}</h3>
      {data.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No data yet</p>
      ) : (
        <div className="space-y-2">
          {data.slice(0, 8).map(d => (
            <div key={d.name} className="flex items-center gap-2">
              <span className="text-xs text-foreground w-28 truncate shrink-0">{d.name}</span>
              <div className="flex-1 bg-muted rounded-full h-2">
                <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${(d.count / max) * 100}%` }} />
              </div>
              <span className="text-xs text-muted-foreground w-8 text-right">{d.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EngagementInsights() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !isManagerPlus(user.role)) return;
    getEngagementInsights({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!isManagerPlus(user?.role)) {
    return (
      <div className="px-4 md:px-8 py-12 text-center">
        <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
        <h1 className="text-lg font-semibold">Access Restricted</h1>
        <p className="text-sm text-muted-foreground mt-1">Engagement insights are available to Managers, Admins, and Owners.</p>
      </div>
    );
  }

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" /> Engagement & Feedback Insights
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Recognition, feedback, and staff engagement overview</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Recognition Given" value={data.recognition.total} icon={Star} />
        <StatCard label="Feedback Items" value={data.feedback.total} icon={MessageSquare} />
        <StatCard label="Avg Read Rate" value={`${data.announcements.avgReadRate}%`} icon={Megaphone} />
        <StatCard label="Rewards Redeemed" value={data.redemptions.total} icon={Gift} />
      </div>

      {/* Status summaries */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recognition</h3>
          <div className="flex items-center gap-4 text-sm">
            <div><span className="font-bold text-primary">{data.recognition.approved}</span> <span className="text-muted-foreground">approved</span></div>
            <div><span className="font-bold text-accent-foreground">{data.recognition.pending}</span> <span className="text-muted-foreground">pending</span></div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Feedback</h3>
          <div className="flex items-center gap-4 text-sm">
            <div><span className="font-bold text-primary">{data.feedback.open}</span> <span className="text-muted-foreground">open</span></div>
            <div><span className="font-bold text-foreground">{data.feedback.actioned}</span> <span className="text-muted-foreground">actioned</span></div>
            <div><span className="font-bold text-muted-foreground">{data.feedback.parked}</span> <span className="text-muted-foreground">parked</span></div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Rewards</h3>
          <div className="flex items-center gap-4 text-sm">
            <div><span className="font-bold text-primary">{data.redemptions.pending}</span> <span className="text-muted-foreground">pending</span></div>
            <div><span className="font-bold text-foreground">{data.redemptions.pointsRedeemed}</span> <span className="text-muted-foreground">pts redeemed</span></div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BarChart data={data.recognition.byCategory.map(c => ({ name: c.category, count: c.count }))} label="Recognition by Category" />
        <BarChart data={data.recognition.topNominees.map(n => ({ name: n.name, count: n.count }))} label="Top Recognised Staff" />
        <BarChart data={data.feedback.byType.map(t => ({ name: t.type, count: t.count }))} label="Feedback by Type" />
        <BarChart data={data.feedback.byCategory.map(c => ({ name: c.category, count: c.count }))} label="Feedback by Category" />
      </div>
    </div>
  );
}
